import { captureElements, elementLabel } from "@cva/shared";
import { toast } from "sonner";
import { create } from "zustand";
import { errorMessage } from "../lib/utils";
import { useEnv } from "./env";
import { useLibraryActions } from "./library";
import { NameDialog } from "./NameDialog";
import { useEditorApi } from "./store";

/**
 * "Als Baustein speichern": saves the selection for the whole organization
 * (Bausteine → Meine Bausteine). Opened from the context menu, the
 * properties panel or the layers.
 */

const useSaveBlock = create<{
  ids: string[] | null;
  open: (ids: string[]) => void;
  close: () => void;
}>((set) => ({
  ids: null,
  open: (ids) => set({ ids }),
  close: () => set({ ids: null }),
}));

/** Opens the dialog for these elements (default: the selection). */
export function requestSaveBlock(ids: string[]) {
  if (ids.length > 0) useSaveBlock.getState().open(ids);
}

export function SaveBlockDialog() {
  const store = useEditorApi();
  const env = useEnv();
  const ids = useSaveBlock((s) => s.ids);
  const close = useSaveBlock((s) => s.close);
  const { save } = useLibraryActions(env.orgId);
  const doc = store.getState().doc;
  const first = ids?.[0] ? doc.elements[ids[0]] : undefined;
  const suggestion =
    ids && ids.length === 1 && first
      ? (first.name ?? elementLabel(first).replace(/[„“]/g, ""))
      : "Mein Baustein";
  return (
    <NameDialog
      open={ids !== null}
      onOpenChange={(open) => !open && close()}
      title="Als Baustein speichern"
      description="Der Baustein steht danach allen im Team unter „Bausteine → Meine Bausteine“ zur Verfügung – in jedem Format, in Videos mit seinen Animationen."
      initial={suggestion.slice(0, 120)}
      pending={save.isPending}
      onConfirm={async (name) => {
        let data: ReturnType<typeof captureElements>;
        try {
          data = captureElements(store.getState().doc, ids ?? []);
        } catch (error) {
          toast.error(errorMessage(error));
          throw error;
        }
        if (!data) {
          toast.info("Untertitel lassen sich nicht als Baustein speichern.");
          return;
        }
        await save.mutateAsync({ kind: "element", name, data });
      }}
    />
  );
}
